import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { displayPrice } from "@/data/catalog";

const CartContext = createContext(null);
const STORAGE_KEY = "cahvac_cart_v2";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, qty = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((x) => x.id === product.id);
      if (idx > -1) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + qty };
        return next;
      }
      return [
        ...prev,
        {
          id: product.id,
          slug: product.slug,
          name: product.name,
          brandSlug: product.brandSlug,
          model: product.model,
          priceVisibility: product.priceVisibility,
          price: product.price,
          salePrice: product.salePrice,
          clearance: product.clearance,
          image: (product.images && product.images[0]) || null,
          qty,
        },
      ];
    });
  }, []);

  const removeItem = useCallback((id) => setItems((prev) => prev.filter((x) => x.id !== id)), []);
  const updateQty = useCallback((id, qty) => setItems((prev) => prev.map((x) => (x.id === id ? { ...x, qty: Math.max(1, qty) } : x))), []);
  const clear = useCallback(() => setItems([]), []);

  const count = useMemo(() => items.reduce((s, x) => s + x.qty, 0), [items]);
  const hasRequestPrice = useMemo(() => items.some((x) => displayPrice(x).mode === "request"), [items]);
  const subtotal = useMemo(
    () =>
      items.reduce((s, x) => {
        const dp = displayPrice(x);
        return s + (dp.mode === "show" ? dp.price * x.qty : 0);
      }, 0),
    [items]
  );

  const value = { items, addItem, removeItem, updateQty, clear, count, subtotal, hasRequestPrice };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
